import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { api } from "services/api";
import { clearCachedStoreInfo, getCachedStoreInfo, setCachedStoreInfo } from "utils/storeInfoCache";

interface StoreLimits {
 products?: number;
 orders?: number;
 pages?: number;
 categories?: number;
 users?: number;
 mediaSpace?: number; // Media space limit in bytes
}

interface StoreUsage {
 products: number;
 orders: number;
 pages: number;
 categories: number;
 users: number;
 mediaSpace?: number; // Media space usage in bytes
 mediaSpaceInMB?: number; // Media space usage in MB (for display)
}

interface StoreThemePalette {
 primary?: string;
 secondary?: string;
 accent?: string;
 background?: string;
 text?: string;
 [key: string]: string | undefined;
}

interface StoreThemeStyles {
 borderRadius?: "none" | "sm" | "md" | "lg" | "xl" | "full";
 spacing?: "compact" | "normal" | "loose";
 shadow?: "none" | "sm" | "md" | "lg" | "xl";
 borderWidth?: "thin" | "normal" | "thick";
 contentWidth?: "narrow" | "medium" | "wide" | "full";
}

interface PayoutDetails {
 bankName?: string;
 accountHolderName?: string;
 accountNumber?: string;
 routingNumber?: string;
 iban?: string;
 swiftCode?: string;
 bankAddress?: string;
 bankCountry?: string;
 paypalEmail?: string;
 stripeConnectAccountId?: string;
 [key: string]: any;
}

interface PayoutPreferences {
 preferredPayoutMethod?: "bank_transfer" | "stripe_connect" | "paypal" | "manual";
 payoutDetails?: PayoutDetails;
}

interface PaymentMethods {
 cashOnDelivery?: boolean;
 cashOnPickup?: boolean;
 payByInvoice?: boolean;
 onlinePayment?: boolean;
}

interface PaymentMethodsByCountry {
 [countryCode: string]: PaymentMethods;
}

interface Store {
 _id: string;
 name: string;
 domain: string;
 subdomain?: string;
  theme: string;
  logo?: string;
  themePalette?: StoreThemePalette;
  themeStyles?: StoreThemeStyles;
  fontFamily?: string;
  allowedDesignerFonts?: string[];
  supportedLanguages?: string[];
 businessModel: "plugin-based" | "pay-as-you-go";
 subscriptionStatus: string;
 limits: StoreLimits;
 isActive: boolean;
 payoutPreferences?: PayoutPreferences;
 paymentMethods?: PaymentMethods; // Legacy: kept for backward compatibility
 paymentMethodsByCountry?: PaymentMethodsByCountry; // New: country-specific payment methods
  settings?: {
    mandatoryAddressFields?: string[]; // Legacy: global mandatory address fields
    mandatoryAddressFieldsByCountry?: Record<string, string[]>; // Country code -> mandatory address fields
    [key: string]: any;
  };
}

interface StoreContextType {
 store: Store | null;
 usage: StoreUsage | null;
 enabledPlugins: string[];
 loading: boolean;
 error: string | null;
 refreshStore: () => Promise<void>;
 silentRefreshStore: () => Promise<void>;
 isPluginEnabled: (pluginId: string) => boolean;
}

export const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const useStore = () => {
 const context = useContext(StoreContext);
 if (!context) {
  throw new Error("useStore must be used within a StoreProvider");
 }
 return context;
};

interface StoreProviderProps {
 children: ReactNode;
}

interface IStoreInfoApiResponse {
 store: Store;
 usage: StoreUsage;
 enabledPlugins?: string[];
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
 const [store, setStore] = useState<Store | null>(null);
 const [usage, setUsage] = useState<StoreUsage | null>(null);
 const [enabledPlugins, setEnabledPlugins] = useState<string[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 // Update CSS variables when store theme changes
 // Always updates theme variables, even if they're already set (ensures consistency)
 const updateThemeVariables = (store: Store | null) => {
  if (typeof document === "undefined") return;
  
  const root = document.documentElement;
  const theme = store?.themePalette || {};
  const themeStyles = store?.themeStyles || {};
  const font =
   store?.fontFamily ||
   'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif';
  
  // Loading colors (neutral grays) as fallbacks
  const loadingColors = {
    primary: "#9CA3AF",
    secondary: "#D1D5DB",
    accent: "#E5E7EB",
    background: "#F9FAFB",
    text: "#6B7280",
  };
  
  // Default theme styles
  const defaultStyles = {
    borderRadius: "md",
    spacing: "normal",
    shadow: "md",
    borderWidth: "normal",
    contentWidth: "wide",
  };
  
  // Map borderRadius values to CSS values
  const borderRadiusMap: Record<string, string> = {
    none: "0",
    sm: "0.125rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    full: "9999px",
  };
  
  // Map spacing values to CSS multipliers (used as base spacing unit)
  const spacingMap: Record<string, string> = {
    compact: "0.75",
    normal: "1",
    loose: "1.5",
  };
  
  // Map shadow values to CSS box-shadow
  const shadowMap: Record<string, string> = {
    none: "none",
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  };
  
  // Map borderWidth values to CSS values
  const borderWidthMap: Record<string, string> = {
    thin: "1px",
    normal: "2px",
    thick: "4px",
  };
  
  // Map contentWidth values to CSS max-width values
  const contentWidthMap: Record<string, string> = {
    narrow: "48rem", // 768px - max-w-3xl
    medium: "64rem", // 1024px - max-w-4xl
    wide: "80rem", // 1280px - max-w-7xl
    full: "100%", // Full width
  };
  
  // Escape CSS values to prevent injection
  const escapeCssValue = (value: string | undefined): string => {
    if (!value) return "";
    return String(value)
      .replace(/[<>]/g, "")
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, "") // NOSONAR - necessary to sanitize CSS values
      .trim();
  };
  
  // Always update CSS variables (even if already set) to ensure consistency
  // This ensures client-side always has the latest theme, even if server-side injection worked
  root.style.setProperty("--store-primary", escapeCssValue(theme.primary || loadingColors.primary));
  root.style.setProperty("--store-secondary", escapeCssValue(theme.secondary || loadingColors.secondary));
  root.style.setProperty("--store-accent", escapeCssValue(theme.accent || loadingColors.accent));
  root.style.setProperty("--store-background", escapeCssValue(theme.background || loadingColors.background));
  root.style.setProperty("--store-text", escapeCssValue(theme.text || loadingColors.text));
  root.style.setProperty("--store-font-family", escapeCssValue(font));
  
  // Theme styles CSS variables
  const borderRadius = themeStyles.borderRadius || defaultStyles.borderRadius;
  const spacing = themeStyles.spacing || defaultStyles.spacing;
  const shadow = themeStyles.shadow || defaultStyles.shadow;
  const borderWidth = themeStyles.borderWidth || defaultStyles.borderWidth;
  const contentWidth = themeStyles.contentWidth || defaultStyles.contentWidth;
  
  root.style.setProperty("--store-border-radius", escapeCssValue(borderRadiusMap[borderRadius] || borderRadiusMap[defaultStyles.borderRadius]));
  root.style.setProperty("--store-spacing-multiplier", escapeCssValue(spacingMap[spacing] || spacingMap[defaultStyles.spacing]));
  root.style.setProperty("--store-shadow", escapeCssValue(shadowMap[shadow] || shadowMap[defaultStyles.shadow]));
  root.style.setProperty("--store-border-width", escapeCssValue(borderWidthMap[borderWidth] || borderWidthMap[defaultStyles.borderWidth]));
  root.style.setProperty("--store-content-width", escapeCssValue(contentWidthMap[contentWidth] || contentWidthMap[defaultStyles.contentWidth]));
 };

 const fetchStore = async (options?: { silent?: boolean; force?: boolean }) => {
  const silent = options?.silent ?? false;
  const force = options?.force ?? false;

  try {
   if (!silent) {
    setLoading(true);
   }
   setError(null);

   if (!force) {
    const cached = getCachedStoreInfo<IStoreInfoApiResponse>();
    if (cached?.store) {
     setStore(cached.store);
     setUsage(cached.usage);
     setEnabledPlugins(cached.enabledPlugins || []);
     return;
    }
   }

   // Fetch store information from API
   const response = await api.get<IStoreInfoApiResponse>("/store/info");

   if (response.data) {
    const storeData = response.data.store;
    setStore(storeData);
    setUsage(response.data.usage);
    setEnabledPlugins(response.data.enabledPlugins || []);
    setCachedStoreInfo<IStoreInfoApiResponse>({
     store: response.data.store,
     usage: response.data.usage,
     enabledPlugins: response.data.enabledPlugins || [],
    });
    
    // Always update CSS variables when store data is loaded (lazy update)
    // This ensures theme is applied even if server-side injection failed or was incomplete
    // Always apply theme regardless of whether server-side injection worked
    // Use requestIdleCallback for lazy execution to avoid blocking render
    if (typeof requestIdleCallback !== "undefined") {
     requestIdleCallback(
      () => {
       updateThemeVariables(storeData);
      },
      { timeout: 1000 }
     );
    } else if (typeof requestAnimationFrame !== "undefined") {
     requestAnimationFrame(() => {
      updateThemeVariables(storeData);
     });
    } else {
     // Fallback for environments without requestIdleCallback or requestAnimationFrame
     setTimeout(() => {
      updateThemeVariables(storeData);
     }, 0);
    }
   } else {
    setError("Failed to load store information");
   }
  } catch (err: any) {
   // Store info endpoint might not be available, that's okay
   // Only set error if it's not a 404 or network error
   if (err.response?.status !== 404) {
    setError(err.message || "Failed to load store");
   }
   if (force) {
    // If we're forcing a refresh and it fails, clear cache to avoid keeping stale data forever
    clearCachedStoreInfo();
   }
  } finally {
   if (!silent) {
    setLoading(false);
   }
  }
 };

 useEffect(() => {
  fetchStore({ silent: false, force: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 // Update theme variables whenever store changes
 // This ensures theme is always up-to-date, even if store data changes
 // Always applies theme lazily, regardless of whether server-side injection worked
 useEffect(() => {
  if (store) {
   // Use requestIdleCallback for lazy execution to avoid blocking render
   // This ensures theme is applied without impacting performance
   if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(
     () => {
      updateThemeVariables(store);
     },
     { timeout: 1000 }
    );
   } else if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => {
     updateThemeVariables(store);
    });
   } else {
    setTimeout(() => {
     updateThemeVariables(store);
    }, 0);
   }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [store]);

 const refreshStore = async () => {
  await fetchStore({ silent: false, force: true });
 };

 const silentRefreshStore = async () => {
  await fetchStore({ silent: true, force: true });
 };

 const isPluginEnabled = (pluginId: string): boolean => {
  // For pay-as-you-go stores, all features are enabled by default
  if (store?.businessModel === "pay-as-you-go") {
   return true;
  }
  // For plugin-based stores, check if plugin is in enabledPlugins array
  return enabledPlugins.includes(pluginId);
 };

 return (
  <StoreContext.Provider value={{ store, usage, enabledPlugins, loading, error, refreshStore, silentRefreshStore, isPluginEnabled }}>
   {children}
  </StoreContext.Provider>
 );
};

